import { useMemo } from "react";
import { Link } from "react-router-dom";
import Badge from "@/components/common/Badge";
import ActionMenu from "@/components/common/ActionMenu";
import DataTable, { DATA_TABLE_CLASSES } from "@/components/common/DataTable";
import RowIcon from "@/components/common/RowIcon";
import FillBar from "@/components/common/FillBar";
import AnimatedNumber from "@/components/common/AnimatedNumber";
import { DEFAULT_CAMPAIGN_ICON, nameOr } from "@/utils/itemIcon";
import { getRowIcon } from "@/utils/getRowIcon";
import { campaignBudgetUsed } from "@/utils/calculations";
import { CheckCircle2, Pencil, Trash2, UserRoundCog } from "lucide-react";
import { CampaignType } from "@adlywise/shared";

const STATUS_VARIANT = {
  active: "success",
  completed: "info",
  paused: "warn",
  draft: "neutral",
  pending: "neutral",
};

const DATE_FORMAT = { month: "short", day: "numeric", year: "numeric" };

const initialsOf = (name) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const formatDate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", DATE_FORMAT);
};

const formatCurrency = (value) =>
  `$${(Number(value) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatNumber = (n) => (n ?? 0).toLocaleString("en-US");

const formatPct = (n) => `${Math.round(Number(n) || 0)}%`;

export default function CampaignsTable({
  campaigns,
  users = [],
  segments = [],
  stats = {},
  loadingUuids,
  showAuthor = false,
  onEdit,
  onDelete,
  onApprove,
  onTransfer,
  emptyMessage = "No campaigns to display.",
}) {
  const usersById = useMemo(() => {
    const map = new Map();
    for (const u of users) map.set(u.uuid, u);
    return map;
  }, [users]);

  const segmentsById = useMemo(() => {
    const map = new Map();
    for (const s of segments) map.set(s.uuid, s);
    return map;
  }, [segments]);

  const showActions = Boolean(onEdit || onDelete || onApprove || onTransfer);
  const columnCount =
    1 /* name */ +
    (showAuthor ? 1 : 0) +
    7 /* status, timeframe, segments, impressions, devices, budget, creative */ +
    (showActions ? 1 : 0);

  return (
    <DataTable>
      <thead>
        <tr className={DATA_TABLE_CLASSES.headRow}>
          <th className={DATA_TABLE_CLASSES.th}>Name</th>
          {showAuthor && <th className={DATA_TABLE_CLASSES.th}>Author</th>}
          <th className={DATA_TABLE_CLASSES.th}>Status</th>
          <th className={DATA_TABLE_CLASSES.th}>Timeframe</th>
          <th className={DATA_TABLE_CLASSES.th}>Segments</th>
          <th className={`${DATA_TABLE_CLASSES.th} w-[200px]`}>
            Impressions / Bids
          </th>
          <th className={DATA_TABLE_CLASSES.th}>Devices</th>
          <th className={`${DATA_TABLE_CLASSES.th} w-[200px]`}>Budget used</th>
          <th className={DATA_TABLE_CLASSES.th}>Creative</th>
          {showActions && (
            <th className={DATA_TABLE_CLASSES.thRight}>Actions</th>
          )}
        </tr>
      </thead>
      <tbody>
        {campaigns.length === 0 ? (
          <tr>
            <td colSpan={columnCount} className={DATA_TABLE_CLASSES.emptyCell}>
              {emptyMessage}
            </td>
          </tr>
        ) : (
          campaigns.map((campaign) => {
            const parsed = getRowIcon(campaign, DEFAULT_CAMPAIGN_ICON);
            const Icon = parsed.Icon;
            const displayName = nameOr(parsed.displayName, "Untitled campaign");
            const rowStats = stats[campaign.uuid];
            const isLoading =
              (loadingUuids && loadingUuids.has(campaign.uuid)) ||
              rowStats === undefined;
            // `confirmed` = serves whose request fired an impression beacon.
            // `served` = the number of BIDS/ad responses we returned (every
            // adResponse row) — bids to the SSP for transit, ad responses
            // served for VAST. The cell shows impressions over bids as a fill
            // rate for BOTH types. Both come from the same aggregate with no
            // deviceId gate, so the ratio is bounded at 100 %; `totalReceivers`
            // is deviceId-gated (a receivers figure) and is NOT the denominator.
            const served = Number(rowStats?.served ?? 0);
            const confirmed = Number(rowStats?.confirmedImpressions ?? 0);
            const fillRate =
              served > 0 ? Math.round((confirmed / served) * 100) : 0;
            // Views = serves whose request fired the player start beacon;
            // VTR = views over confirmed impressions, clamped: a player can
            // drop the impression beacon yet still report start. Only standard
            // VAST campaigns have a player (popunders don't; transit/RTB serves
            // never fire tracking events), so the line is hidden otherwise.
            const views = Number(rowStats?.views ?? 0);
            const vtr =
              confirmed > 0
                ? Math.min(100, Math.round((views / confirmed) * 100))
                : 0;
            const showViews =
              !pop && Number(campaign.type ?? 0) === CampaignType.Standard;
            const devices = Number(rowStats?.uniqueReceivers ?? 0);
            // Budget used = real ad_served spend (Σ buyPrice / 1000), same basis
            // as the income board — not impressions × cpm (0 for transit).
            const budgetUsed = campaignBudgetUsed(rowStats);
            const budgetCap = Number(campaign.budgetCap ?? 0);
            // Popunder only: ledger figures (reserved budget, billed pops,
            // invalid visits and the billed-vs-confirmed gap).
            const pop = rowStats?.pop;
            const ivtPct =
              pop && pop.visits > 0 ? (pop.ivtVisits / pop.visits) * 100 : 0;
            const author = campaign.userId
              ? usersById.get(campaign.userId)
              : null;
            const authorName = author?.username ?? campaign.createdBy ?? "—";
            const animated = campaign.status !== "completed";
            return (
              <tr
                key={campaign.uuid}
                onDoubleClick={onEdit ? () => onEdit(campaign) : undefined}
                className={`${DATA_TABLE_CLASSES.bodyRow}${
                  onEdit ? " cursor-pointer" : ""
                }`}
              >
                <td className={DATA_TABLE_CLASSES.td}>
                  <div className="flex items-center gap-[13px]">
                    <RowIcon icon={Icon} />
                    <span className="text-[13.5px] font-bold text-(--text-primary)">
                      {displayName}
                    </span>
                  </div>
                </td>
                {showAuthor && (
                  <td className={DATA_TABLE_CLASSES.td}>
                    {authorName === "—" ? (
                      <span className="text-(--text-muted)">—</span>
                    ) : (
                      <span className="inline-flex items-center gap-2 rounded-(--radius-pill) bg-(--surface-2) py-1 pl-1 pr-3">
                        <span className="flex h-6 w-6 items-center justify-center rounded-(--radius-pill) bg-(--brand-primary) text-[10px] font-bold text-(--brand-primary-contrast)">
                          {initialsOf(authorName)}
                        </span>
                        <span className="text-[12.5px] font-semibold text-(--text-primary)">
                          {authorName}
                        </span>
                      </span>
                    )}
                  </td>
                )}
                <td className={DATA_TABLE_CLASSES.td}>
                  <Badge variant={STATUS_VARIANT[campaign.status] ?? "neutral"}>
                    {campaign.status}
                  </Badge>
                </td>
                <td className={DATA_TABLE_CLASSES.td}>
                  {(() => {
                    const start = formatDate(campaign.startDate);
                    const end = formatDate(campaign.endDate);
                    if (!start && !end) {
                      return <span className="text-(--text-muted)">—</span>;
                    }
                    return (
                      <div className="text-[12.5px] leading-tight text-(--text-primary)">
                        <div>{start || "—"}</div>
                        <div className="text-(--text-muted)">
                          {end ? `→ ${end}` : "→ no end"}
                        </div>
                      </div>
                    );
                  })()}
                </td>
                <td className={DATA_TABLE_CLASSES.td}>
                  {(() => {
                    const ids = Array.isArray(campaign.selectedSegments)
                      ? campaign.selectedSegments
                      : [];
                    if (ids.length === 0) {
                      return <span className="text-(--text-muted)">—</span>;
                    }
                    return (
                      <div className="flex max-w-[240px] flex-col text-[12.5px] leading-tight">
                        {ids.map((id) => {
                          const seg = segmentsById.get(id);
                          const label = seg?.name || `${id.slice(0, 6)}…`;
                          return (
                            <Link
                              key={id}
                              to={`/dashboard/segments/${id}/edit`}
                              className="block truncate text-(--text-primary) no-underline hover:underline"
                              title={seg?.name || id}
                            >
                              {label}
                            </Link>
                          );
                        })}
                      </div>
                    );
                  })()}
                </td>
                <td className={DATA_TABLE_CLASSES.td}>
                  <div className="w-[160px]">
                    <div className="text-[13.5px] font-bold text-(--text-primary)">
                      {isLoading ? (
                        <span className="text-(--text-muted)">…</span>
                      ) : (
                        <>
                          <AnimatedNumber value={confirmed} />
                          <span className="ml-1 text-[11.5px] font-medium text-(--text-muted)">
                            / {formatNumber(served)} bids ({fillRate}% fill)
                          </span>
                        </>
                      )}
                    </div>
                    {!isLoading && (
                      <FillBar
                        used={confirmed}
                        cap={served}
                        animated={animated}
                      />
                    )}
                    {!isLoading && showViews && (
                      <div
                        className="mt-1 text-[11.5px] text-(--text-muted)"
                        title="Views: serves whose player fired the start event (tracked since 2026-10-16, Adlywise-served VAST only). VTR = views / impressions."
                      >
                        {formatNumber(views)} views · VTR {vtr}%
                      </div>
                    )}
                    {!isLoading && pop && (
                      <div
                        className="mt-1 text-[11.5px] text-(--text-muted)"
                        title="Billed: pops the partner billed. Gap: pops billed over 24 h ago that we could not confirm as a visit. IVT: invalid visits."
                      >
                        {formatNumber(pop.billed)} billed · gap{" "}
                        {pop.gapPct === null ? "—" : formatPct(pop.gapPct)} ·
                        IVT {formatPct(ivtPct)}
                      </div>
                    )}
                  </div>
                </td>
                <td className={DATA_TABLE_CLASSES.td}>
                  <div className="text-[13.5px] font-bold text-(--text-primary)">
                    {isLoading ? (
                      <span className="text-(--text-muted)">…</span>
                    ) : (
                      <AnimatedNumber value={devices} />
                    )}
                  </div>
                </td>
                <td className={DATA_TABLE_CLASSES.td}>
                  <div className="w-[160px]">
                    <div className="text-[13.5px] font-bold text-(--text-primary)">
                      {isLoading ? (
                        <span className="text-(--text-muted)">…</span>
                      ) : (
                        <>
                          <AnimatedNumber
                            value={budgetUsed}
                            format={(n) => formatCurrency(n)}
                          />
                          {budgetCap > 0 && (
                            <span className="ml-1 text-[11.5px] font-medium text-(--text-muted)">
                              / {formatCurrency(budgetCap)}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    {!isLoading && (
                      <FillBar
                        used={budgetUsed}
                        cap={budgetCap}
                        animated={animated}
                      />
                    )}
                    {!isLoading && pop && pop.reserved > 0 && (
                      <div
                        className="mt-1 text-[11.5px] text-(--text-muted)"
                        title="Bid price of wins the partner has not billed yet (released after 24 h)."
                      >
                        + {formatCurrency(pop.reserved)} reserved
                      </div>
                    )}
                  </div>
                </td>
                <td className={DATA_TABLE_CLASSES.td}>
                  {Array.isArray(campaign.creatives) &&
                  campaign.creatives.length > 0 ? (
                    <div className="flex max-w-[240px] flex-col text-[12.5px] leading-tight">
                      {campaign.creatives.map((creative) => {
                        const label =
                          creative.name?.trim() ||
                          creative.url ||
                          creative.uuid;
                        return (
                          <Link
                            key={creative.uuid}
                            to={`/dashboard/creatives?edit=${creative.uuid}`}
                            className="block truncate text-(--text-primary) no-underline hover:underline"
                            title={creative.url || label}
                          >
                            {label}
                          </Link>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="text-(--text-muted)">No creative</span>
                  )}
                </td>
                {showActions && (
                  <td className={DATA_TABLE_CLASSES.tdRight}>
                    <ActionMenu
                      ariaLabel={`Actions for ${displayName}`}
                      items={[
                        onApprove &&
                          campaign.status === "pending" && {
                            label: "Approve",
                            icon: CheckCircle2,
                            onClick: () => onApprove(campaign),
                          },
                        onTransfer && {
                          label: "Transfer owner",
                          icon: UserRoundCog,
                          onClick: () => onTransfer(campaign),
                        },
                        onEdit && {
                          label: "Edit",
                          icon: Pencil,
                          onClick: () => onEdit(campaign),
                        },
                        onDelete && {
                          label: "Delete",
                          icon: Trash2,
                          danger: true,
                          onClick: () => onDelete(campaign),
                        },
                      ].filter(Boolean)}
                    />
                  </td>
                )}
              </tr>
            );
          })
        )}
      </tbody>
    </DataTable>
  );
}
